import React, { useEffect, useState, useMemo } from "react";
import { adminAPI } from "../services/api";
import "../AdminOrders.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CheckCircle,
  RefreshCw,
  User,
  IndianRupee,
  Loader2,
  ClipboardCheck,
  PackageOpen,
  BarChart3,
  Lock,
  CreditCard,
  Clock,
  Filter,
  ArrowUpDown,
  AlertCircle,
  CheckSquare,
} from "lucide-react";

const ManageOrders = () => {
  const [orders, setOrders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ordersRes, partnersRes] = await Promise.all([
        adminAPI.getAllOrders(),
        adminAPI.getDeliveryPartners(),
      ]);
      setOrders(ordersRes.data);
      setPartners(partnersRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load data. Please check backend connectivity.");
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (orderId, partnerId) => {
    if (!partnerId) {
      toast.warning("Please select a delivery partner.");
      return;
    }

    try {
      await adminAPI.assignDelivery(orderId, partnerId);
      const partner = partners.find((p) => p._id === partnerId);
      toast.success(`Order assigned to ${partner?.name || "partner"} successfully!`);
      await loadData();
    } catch (error) {
      console.error("Error assigning delivery partner:", error);
      toast.error("Failed to assign order. Please try again.");
    }
  };

  // ========== FILTER & SORT LOGIC ==========
  const filteredOrders = useMemo(() => {
    let filtered = [...orders];

    switch (filter) {
      case "paid":
        filtered = filtered.filter((o) => o.isPaid);
        break;
      case "unpaid":
        filtered = filtered.filter((o) => !o.isPaid);
        break;
      case "delivered":
        filtered = filtered.filter((o) => o.isDelivered);
        break;
      case "pending":
        filtered = filtered.filter((o) => !o.isDelivered && !o.isCanceled);
        break;
      case "cancelled":
        filtered = filtered.filter((o) => o.isCanceled);
        break;
      case "assigned":
        filtered = filtered.filter((o) => !!o.assignedTo);
        break;
      default:
        break;
    }

    switch (sortBy) {
      case "oldest":
        filtered.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
        break;
      case "priceHigh":
        filtered.sort((a, b) => b.totalPrice - a.totalPrice);
        break;
      case "priceLow":
        filtered.sort((a, b) => a.totalPrice - b.totalPrice);
        break;
      default:
        filtered.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        break;
    }

    return filtered;
  }, [orders, filter, sortBy]);

  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 text-secondary">
        <Loader2 className="me-2 spinner-border text-primary" />
        Loading orders...
      </div>
    );

  const totalOrders = orders.length;
  const deliveredOrders = orders.filter((o) => o.isDelivered).length;
  const pendingOrders = orders.filter( (o) => !o.isDelivered && !o.isCanceled).length;

  return (
    <div className="container py-4">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={true} closeOnClick={true} rtl={false} pauseOnFocusLoss={true} draggable={true} />
      
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-semibold text-dark d-flex align-items-center gap-2">
          <BarChart3 size={26} className="text-primary" />
          Manage Orders
        </h2>
        <button
          onClick={loadData}
          className="btn btn-primary d-flex align-items-center gap-2"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* KPI CARDS */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card border-start border-primary border-4 shadow-sm h-100" style={{transition: "transform 0.2s, box-shadow 0.2s", cursor: "pointer"}} onMouseEnter={(e) => {e.currentTarget.style.transform = "translateY(-5px)"; e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.15)";}} onMouseLeave={(e) => {e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "";}}>
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <p className="text-muted mb-1">Total Orders</p>
                <h4 className="fw-bold">{totalOrders}</h4>
              </div>
              <ClipboardCheck size={30} className="text-primary" />
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card border-start border-success border-4 shadow-sm h-100" style={{transition: "transform 0.2s, box-shadow 0.2s", cursor: "pointer"}} onMouseEnter={(e) => {e.currentTarget.style.transform = "translateY(-5px)"; e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.15)";}} onMouseLeave={(e) => {e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "";}}>
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <p className="text-muted mb-1">Delivered</p>
                <h4 className="fw-bold text-success">{deliveredOrders}</h4>
              </div>
              <CheckCircle size={30} className="text-success" />
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card border-start border-danger border-4 shadow-sm h-100" style={{transition: "transform 0.2s, box-shadow 0.2s", cursor: "pointer"}} onMouseEnter={(e) => {e.currentTarget.style.transform = "translateY(-5px)"; e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.15)";}} onMouseLeave={(e) => {e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "";}}>
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <p className="text-muted mb-1">Pending</p>
                <h4 className="fw-bold text-danger">{pendingOrders}</h4>
              </div>
              <PackageOpen size={30} className="text-danger" />
            </div>
          </div>
        </div>
      </div>

      {/* FILTER & SORT BAR */}
      <div className="card shadow-sm border-0 mb-3 p-3 bg-light">
        <div className="row g-2 align-items-center">
          <div className="col-md-6 d-flex align-items-center gap-2">
            <Filter size={18} className="text-primary fw-semibold" />
            <span className="fw-semibold text-secondary">Filter:</span>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="form-select form-select-sm"
              style={{maxWidth: "200px"}}
            >
              <option value="all">All Orders</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="delivered">Delivered</option>
              <option value="pending">Pending</option>
              <option value="assigned">Assigned</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="col-md-6 d-flex align-items-center gap-2">
            <ArrowUpDown size={18} className="text-secondary fw-semibold" />
            <span className="fw-semibold text-secondary">Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="form-select form-select-sm"
              style={{maxWidth: "200px"}}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="priceHigh">Price: High → Low</option>
              <option value="priceLow">Price: Low → High</option>
            </select>
          </div>
        </div>
      </div>

      {/* ORDERS TABLE */}
      <div className="card shadow-sm border-0 overflow-hidden">
        <div className="table-responsive">
          <table className="table align-middle table-hover table-bordered mb-0">
            <thead className="table-dark">
              <tr>
                <th className="fw-bold">Customer</th>
                <th className="fw-bold">Total</th>
                <th className="text-center fw-bold">Payment</th>
                <th className="text-center fw-bold">Status</th>
                <th className="text-center fw-bold">Delivery Partner</th>
                <th className="text-center fw-bold">Action</th>
              </tr>
            </thead>

            <tbody>
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order) => {
                  const isAssigned = !!order.assignedTo;

                  return (
                      <tr
                        key={order._id}
                        className={`${order.isCanceled ? "cancelled-row" : ""} align-middle`}
                        style={{backgroundColor: order.isCanceled ? "#fff5f5" : "", transition: "background-color 0.2s"}}
                      >

                      {/* Customer */}
                      <td className="d-flex align-items-center gap-2">
                        <User size={16} className="text-secondary" />
                        {order.user?.name || "Unknown"}
                      </td>

                      {/* Total */}
                      <td className="fw-medium">
                        <IndianRupee size={14} className="text-muted me-1" />
                        {order.totalPrice?.toFixed(2)}
                      </td>

                      {/* Payment */}
                      <td className="text-center">
                        {order.isPaid ? (
                          <span className="badge bg-success d-flex justify-content-center align-items-center gap-1">
                            <CreditCard size={14} />
                            Paid
                          </span>
                        ) : (
                          <span className="badge bg-warning text-dark d-flex justify-content-center align-items-center gap-1">
                            <Clock size={14} />
                            Pending
                          </span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="text-center">
                        {order.isCanceled ? (
                          <div className="d-flex justify-content-center align-items-center gap-2">
                            <span className="badge bg-danger d-flex align-items-center gap-1">
                              <AlertCircle size={14} />
                              Cancelled
                            </span>
                            {order.cancelReason && (
                              <span
                                title={order.cancelReason}
                                className="text-danger fw-bold"
                                style={{
                                  cursor: "help",
                                  fontSize: "14px",
                                }}
                              >
                                ⓘ
                              </span>
                            )}
                          </div>
                        ) : order.isDelivered ? (
                          <span className="badge bg-success d-flex align-items-center justify-content-center gap-1">
                            <CheckSquare size={14} />
                            Delivered
                          </span>
                        ) : (
                          <span className="badge bg-warning text-dark d-flex align-items-center justify-content-center gap-1">
                            <Clock size={14} />
                            Pending
                          </span>
                        )}
                      </td>

                      {/* Delivery Partner */}
                      <td className="text-center">
                        {isAssigned ? (
                          <div className="d-flex justify-content-center align-items-center gap-2 badge bg-info text-dark px-3 py-2 w-100 justify-content-center">
                            <span className="fw-semibold">
                              {order.assignedTo?.name || "Assigned"}
                            </span>
                            <Lock size={14} />
                          </div>
                        ) : order.isCanceled ? (
                          <span className="badge bg-danger">
                            Not Assigned
                          </span>
                        ) : (
                          <select
                            value={order.assignedTo?._id || ""}
                            onChange={(e) =>
                              handleAssign(order._id, e.target.value)
                            }
                            className="form-select form-select-sm"
                            style={{minWidth: "160px"}}
                          >
                            <option value="">Assign Partner</option>
                            {partners.map((p) => (
                              <option key={p._id} value={p._id}>
                                👤 {p.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>

                      {/* Action */}
                      <td className="text-center">
                        <button
                          onClick={loadData}
                          className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-2 rounded-pill px-3"
                          title="Refresh data"
                        >
                          <RefreshCw size={16} />
                          <span className="d-none d-sm-inline">Refresh</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-4">
                    No matching orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManageOrders;
